import { useRef } from 'react';
import type { MouseEvent, KeyboardEvent, FocusEvent } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import {
  Icon,
  registerIcon,
  Button,
  defaultThemeProp,
  useI18n,
  Tooltip,
  tryCatch
} from '@pega/cosmos-react-core';
import * as codeIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/code.icon';

registerIcon(codeIcon);

const StyledSourceCodeButton = styled(Button)<{ $active?: boolean }>(({ theme, $active }) => {
  const activeColor = tryCatch(() =>
    mix(0.85, theme.base.palette['primary-background'], theme.base.palette.interactive)
  );
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: calc(3.5 * ${theme.base.spacing});
    min-width: calc(3.5 * ${theme.base.spacing});
    padding: 0;

    ${$active &&
    css`
      background-color: ${activeColor};
      color: ${theme.base.palette.interactive};
      border-bottom: calc(0.25 * ${theme.base.spacing}) solid ${theme.base.palette.interactive};
    `}
  `;
});

StyledSourceCodeButton.defaultProps = defaultThemeProp;

interface SourceCodeButtonProps {
  active?: boolean;
  onToggle?: (isSourceMode: boolean) => void;
  'data-testid'?: string;
}

const SourceCodeButton = ({ active, onToggle, ...restProps }: SourceCodeButtonProps) => {
  const t = useI18n();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    onToggle?.(!active);
  };

  return (
    <>
      <StyledSourceCodeButton
        {...restProps}
        variant='simple'
        type='button'
        icon={false}
        ref={buttonRef}
        data-toolbar-item='true'
        $active={active}
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          handleToggle();
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleToggle();
          }
        }}
        onFocus={(e: FocusEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        aria-label={t('rte_source_code')}
        aria-pressed={active}
      >
        <Icon name='code' />
      </StyledSourceCodeButton>
      {buttonRef.current && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {t('rte_source_code')}
        </Tooltip>
      )}
    </>
  );
};

export default SourceCodeButton;
